import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/tools/ToolShell";
import { ES_TOOLS, ES_CATEGORIES } from "@/lib/es-tools-catalog";
import { ES_PDF_TOOLS } from "@/lib/es-pdf-catalog";
import { PicaminiLogo } from "@/components/ui/Logo";
import {
  Shuffle,
  Disc3,
  Users,
  Timer,
  FileText,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  Zap,
  Globe,
} from "lucide-react";

export const Route = createFileRoute("/es/")({
  head: () => ({
    meta: [
      { title: "Herramientas Gratis para Profesores, Ruletas Aleatorias y PDF | Picamini.online" },
      {
        name: "description",
        content:
          "Herramientas gratuitas para el aula y toma de decisiones: ruletas de nombres aleatorias, generadores de equipos, temporizadores escolares y utilidades PDF 100% privadas sin registro.",
      },
      {
        name: "keywords",
        content:
          "picamini, selector de nombres aleatorios, ruleta de nombres, ruleta aleatoria, generador de equipos, temporizador para clase, herramientas para profesores gratis, unir pdf gratis, editor pdf online",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "Herramientas Gratis para el Aula y Ruletas Aleatorias | Picamini.online" },
      {
        property: "og:description",
        content:
          "37+ herramientas interactivas gratuitas en el navegador para docentes y facilitadores: ruletas de nombres, equipos, temporizadores y utilidades PDF.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://picamini.online/es/" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { property: "og:locale", content: "es_ES" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Herramientas Gratis para el Aula en Español | Picamini.online" },
      { name: "twitter:description", content: "37+ herramientas gratuitas en el navegador para profesores y docentes." },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://picamini.online/es/" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/" },
      { rel: "alternate", hrefLang: "es", href: "https://picamini.online/es/" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Picamini.online Español",
          "url": "https://picamini.online/es/",
          "inLanguage": "es",
          "description": "Herramientas educativas, ruletas aleatorias y utilidades PDF 100% gratuitas y privadas en el navegador.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Herramientas Destacadas para el Aula y Sorteos",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Selector de Nombres Aleatorios",
              "url": "https://picamini.online/es/selector-de-nombres",
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Ruleta Aleatoria",
              "url": "https://picamini.online/es/ruleta-aleatoria",
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Ruleta de Nombres",
              "url": "https://picamini.online/es/ruleta-de-nombres",
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Generador de Equipos Aleatorios",
              "url": "https://picamini.online/es/generador-de-equipos",
            },
            {
              "@type": "ListItem",
              "position": 5,
              "name": "Temporizador para Clase",
              "url": "https://picamini.online/es/temporizador-para-clase",
            },
            {
              "@type": "ListItem",
              "position": 6,
              "name": "Herramientas PDF",
              "url": "https://picamini.online/es/pdf-tools",
            },
          ],
        }),
      },
    ],
  }),
  component: EsHomepage,
});

function EsHomepage() {
  const featuredTools = ES_TOOLS.filter((t) => t.featured);
  const featuredPdfTools = ES_PDF_TOOLS.filter((t) => t.featured);

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans">
      <SiteHeader lang="es" />

      <main className="flex-1">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background py-16 sm:py-24 border-b border-border/50">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 text-center space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5 text-xs sm:text-sm font-bold text-primary">
              <Sparkles className="size-4" /> 100% Gratis • Sin Registro • Privacidad Total
            </div>

            <h1 className="text-4xl font-extrabold tracking-tight sm:text-6xl text-foreground max-w-4xl mx-auto leading-tight">
              Herramientas Gratuitas para el Aula, <span className="text-primary">Ruletas Aleatorias</span> y Utilidades PDF
            </h1>

            <p className="max-w-3xl mx-auto text-base sm:text-xl text-muted-foreground leading-relaxed">
              Diseñadas para profesores, estudiantes y facilitadores. Selecciona alumnos al azar, gira ruletas de decisiones, crea equipos equilibrados y edita archivos PDF directamente en tu navegador.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
              <a
                href="#herramientas"
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-base font-bold text-primary-foreground shadow-lg transition-transform hover:scale-105"
              >
                Explorar Herramientas <ArrowRight className="size-5" />
              </a>
              <Link
                to="/es/pdf-tools"
                className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-6 py-3.5 text-base font-bold text-foreground transition-colors hover:bg-secondary"
              >
                <FileText className="size-5 text-primary" /> Herramientas PDF
              </Link>
            </div>
          </div>
        </section>

        {/* FEATURED TOOLS GRID */}
        <section id="herramientas" className="py-16 mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center space-y-3">
            <h2 className="text-3xl font-extrabold sm:text-4xl text-foreground">
              Herramientas Más Populares
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              Utilidades interactivas listas para usar en el aula, reuniones o dinámicas de grupo.
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {featuredTools.map((tool) => (
              <Link
                key={tool.slug}
                to="/es/$slug"
                params={{ slug: tool.slug }}
                className="surface-card p-6 rounded-2xl border transition-all hover:-translate-y-1 hover:shadow-lg flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Disc3 className="size-5" />
                    </span>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground px-2 py-0.5 rounded-md bg-secondary">
                      {tool.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-foreground">{tool.name}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{tool.tagline}</p>
                </div>
                <div className="pt-4 flex items-center gap-1.5 text-xs font-bold text-primary">
                  Probar gratis <ArrowRight className="size-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* ALL CATEGORIES */}
        {ES_CATEGORIES.map((cat) => {
          const categoryTools = ES_TOOLS.filter((t) => t.category === cat);
          if (categoryTools.length === 0) return null;

          return (
            <section key={cat} className="py-10 border-t border-border/40 mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-6">
              <h3 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <span className="size-3 rounded-full bg-primary" /> {cat}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {categoryTools.map((tool) => (
                  <Link
                    key={tool.slug}
                    to="/es/$slug"
                    params={{ slug: tool.slug }}
                    className="p-4 rounded-xl border bg-card transition-all hover:bg-secondary/40 hover:-translate-y-0.5 space-y-1.5"
                  >
                    <p className="font-bold text-sm text-foreground">{tool.name}</p>
                    <p className="text-xs text-muted-foreground leading-snug line-clamp-2">{tool.tagline}</p>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}

        {/* PDF TOOLS PREVIEW */}
        <section className="py-16 bg-secondary/30 border-y border-border/50">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-10">
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary">PDF Toolkit Pro</span>
                <h2 className="text-3xl font-extrabold sm:text-4xl text-foreground mt-1">
                  Herramientas PDF 100% Privadas en tu Navegador
                </h2>
                <p className="text-base text-muted-foreground mt-2 max-w-2xl">
                  Une, divide, comprime y edita archivos PDF sin subir tus documentos a ningún servidor.
                </p>
              </div>
              <Link
                to="/es/pdf-tools"
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground"
              >
                Ver todas las herramientas PDF <ArrowRight className="size-4" />
              </Link>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {featuredPdfTools.slice(0, 6).map((pdfTool) => (
                <Link
                  key={pdfTool.slug}
                  to="/es/pdf-tools/$slug"
                  params={{ slug: pdfTool.slug }}
                  className="surface-card p-6 rounded-2xl border bg-card transition-all hover:-translate-y-1 hover:shadow-md space-y-3"
                >
                  <div className="flex items-center gap-3">
                    <span className="grid size-10 place-items-center rounded-xl bg-primary/10 text-primary">
                      <FileText className="size-5" />
                    </span>
                    <div>
                      <h3 className="font-bold text-base text-foreground">{pdfTool.name}</h3>
                      <span className="text-[11px] text-muted-foreground">{pdfTool.category}</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{pdfTool.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* WHY PICAMINI */}
        <section className="py-16 mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center space-y-3">
            <h2 className="text-3xl font-extrabold text-foreground">¿Por qué elegir Picamini.online?</h2>
            <p className="text-base text-muted-foreground max-w-xl mx-auto">
              Diseñado pensando en la privacidad, simplicidad y rapidez para docentes y grupos.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            <div className="p-6 rounded-2xl border bg-card space-y-3">
              <span className="grid size-12 place-items-center rounded-2xl bg-emerald-500/10 text-emerald-500 font-bold">
                <ShieldCheck className="size-6" />
              </span>
              <h3 className="text-lg font-bold text-foreground">100% Privado y Seguro</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Tus listas de alumnos y archivos PDF nunca se envían a servidores externos. Todo el procesamiento se ejecuta en tu propio navegador.
              </p>
            </div>

            <div className="p-6 rounded-2xl border bg-card space-y-3">
              <span className="grid size-12 place-items-center rounded-2xl bg-blue-500/10 text-blue-500 font-bold">
                <Zap className="size-6" />
              </span>
              <h3 className="text-lg font-bold text-foreground">Sin Registro ni Pagos</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Sin cuentas, sin contraseñas y sin tarifas ocultas. Abre la página e interactúa inmediatamente desde cualquier dispositivo.
              </p>
            </div>

            <div className="p-6 rounded-2xl border bg-card space-y-3">
              <span className="grid size-12 place-items-center rounded-2xl bg-purple-500/10 text-purple-500 font-bold">
                <Globe className="size-6" />
              </span>
              <h3 className="text-lg font-bold text-foreground">Optimizado para Pantallas</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Tipografías de alto contraste y animaciones fluidas diseñadas para proyectarse perfectamente en pizarras digitales del aula.
              </p>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter lang="es" />
    </div>
  );
}
